import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import {
  MINIMUM_BILL_GROSZE,
  POINTS_PER_MAJOR_UNIT,
  CONTACT_BONUS_POINTS,
  POINTS_VALIDITY_MONTHS,
  REDEMPTION_VALIDITY_MINUTES,
} from "@/config/loyalty";

/**
 * A reward the guest has claimed but not yet received.
 *
 * Its points are reserved, not spent. They leave the balance only when the
 * reward is actually handed over, which is what `settle` marks. Until the
 * waiter app can report that, a reservation simply lapses at `expiresAt` and
 * the points come back.
 */
export interface PendingRedemption {
  code: string;
  /** One code can cover several rewards claimed in the same go. */
  dishIds: string[];
  points: number;
  expiresAt: string;
}

/**
 * The guest, as agreed:
 *
 *   guest
 *     ├─ lastActivityAt
 *     ├─ points
 *     └─ hashes: [a7f3c9, b2e881, ...]
 *
 * No account, no e-mail, no phone number. The hashes are what identify a
 * returning guest: one per payment instrument they have paid with. The
 * intended source is Viva's token for the instrument; until the payment
 * integration exposes one, `helpers/paymentHash.ts` derives a stand-in from
 * what the checkout knows. See the caveat there: it decides whether a
 * returning guest is recognised or silently becomes a new one.
 *
 * Everything lives in this browser's localStorage. Clearing site data, or
 * paying from a different phone, is a different guest.
 */
export interface LoyaltyGuest {
  /** ISO timestamp of the last payment. Points expire relative to this. */
  lastActivityAt: string | null;
  points: number;
  hashes: string[];
  /** At most one at a time, so a guest cannot reserve their balance twice. */
  pending: PendingRedemption | null;
  /**
   * What the guest chose to leave, kept on this device and nowhere else. See
   * CONTACT_BONUS_POINTS before doing anything with either.
   */
  contactEmail: string | null;
  contactPhone: string | null;
  /**
   * The guest gave an e-mail or a phone number, confirmed it with a code, and
   * their balance was loaded for that identity.
   *
   * Nothing is loaded today: there is no endpoint to load it from, and no code
   * to check the one they typed against. The flag records that the step was
   * completed, so the screen stops asking and the wiring has one place to
   * become real.
   */
  identityVerified: boolean;
  /** So the prompt opens itself once, on a first visit, and not after. */
  identityPromptSeen: boolean;
}

interface LoyaltyState extends LoyaltyGuest {
  /**
   * Award points for a payment and record the instrument.
   * Returns how many points were granted, so the UI can say so.
   */
  registerPayment: (amountGrosze: number, paymentHash?: string) => number;
  /** Spend points immediately, for a reward claimed through the app itself. */
  redeem: (points: number) => boolean;
  /**
   * Reserve points against a code the waiter will scan. Returns the pending
   * redemption, or null when the balance is short or one is already open.
   */
  startRedemption: (
    reward: { code: string; dishIds: string[]; points: number },
  ) => PendingRedemption | null;
  /** The reward was handed over: the reserved points finally leave. */
  settleRedemption: () => void;
  /** Give the code up, whether it lapsed or the guest changed their mind. */
  cancelRedemption: () => void;
  /** Drops a reservation whose code has run out. Points were never taken. */
  releaseExpiredRedemption: () => void;
  /**
   * Record a contact and pay the one-off bonus. Returns the points granted,
   * which is zero when one was already left.
   */
  saveContact: (contact: { email?: string; phone?: string }) => number;
  /** Records a confirmed identity. See identityVerified for what is missing. */
  verifyIdentity: (contact: { email?: string; phone?: string }) => void;
  /** Forgets the contact and the confirmation. Points and hashes stay. */
  signOut: () => void;
  markIdentityPromptSeen: () => void;
  /** Drops the balance if the guest has been away past the validity window. */
  expireIfStale: () => void;
  reset: () => void;
}

const initialGuest: LoyaltyGuest = {
  lastActivityAt: null,
  points: 0,
  hashes: [],
  pending: null,
  contactEmail: null,
  contactPhone: null,
  identityVerified: false,
  identityPromptSeen: false,
};

/** Points not already promised to an open redemption. */
const availableOf = (guest: { points: number; pending: PendingRedemption | null }) =>
  guest.points - (guest.pending?.points ?? 0);

const monthsSince = (isoDate: string): number => {
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return 0;
  const millisecondsPerMonth = 1000 * 60 * 60 * 24 * 30.44;
  return (Date.now() - then) / millisecondsPerMonth;
};

export const useLoyaltyStore = create<LoyaltyState>()(
  persist(
    (set, get) => ({
      ...initialGuest,

      registerPayment: (amountGrosze, paymentHash) => {
        // A bill under the threshold still counts as a visit, so it renews
        // the balance. It just earns nothing.
        const earned =
          amountGrosze >= MINIMUM_BILL_GROSZE
            ? Math.floor(amountGrosze / 100) * POINTS_PER_MAJOR_UNIT
            : 0;

        set((state) => ({
          points: state.points + earned,
          lastActivityAt: new Date().toISOString(),
          hashes:
            paymentHash && !state.hashes.includes(paymentHash)
              ? [...state.hashes, paymentHash]
              : state.hashes,
        }));

        return earned;
      },

      redeem: (points) => {
        const state = get();
        if (points <= 0 || availableOf(state) < points) return false;
        set({ points: state.points - points });
        return true;
      },

      startRedemption: ({ code, dishIds, points }) => {
        const state = get();
        if (state.pending || points <= 0 || availableOf(state) < points) {
          return null;
        }

        const expiresAt = new Date(
          Date.now() + REDEMPTION_VALIDITY_MINUTES * 60 * 1000,
        ).toISOString();
        const pending: PendingRedemption = { code, dishIds, points, expiresAt };
        set({ pending });
        return pending;
      },

      settleRedemption: () => {
        const { pending, points } = get();
        if (!pending) return;
        set({ points: Math.max(0, points - pending.points), pending: null });
      },

      cancelRedemption: () => set({ pending: null }),

      releaseExpiredRedemption: () => {
        const { pending } = get();
        if (!pending) return;
        if (new Date(pending.expiresAt).getTime() <= Date.now()) {
          set({ pending: null });
        }
      },

      saveContact: ({ email, phone }) => {
        const nextEmail = email?.trim() || null;
        const nextPhone = phone?.trim() || null;
        if (!nextEmail && !nextPhone) return 0;

        const state = get();
        // The bonus is one-off: a guest cannot farm it by coming back with a
        // different address. Later details are still recorded, just unpaid.
        const alreadyPaid = Boolean(state.contactEmail || state.contactPhone);

        set({
          contactEmail: nextEmail ?? state.contactEmail,
          contactPhone: nextPhone ?? state.contactPhone,
          points: alreadyPaid ? state.points : state.points + CONTACT_BONUS_POINTS,
        });

        return alreadyPaid ? 0 : CONTACT_BONUS_POINTS;
      },

      verifyIdentity: ({ email, phone }) =>
        set((state) => ({
          contactEmail: email?.trim() || state.contactEmail,
          contactPhone: phone?.trim() || state.contactPhone,
          identityVerified: true,
          identityPromptSeen: true,
        })),

      /**
       * Points belong to this device, not to the contact: they were earned by
       * the payment instruments in `hashes`, so signing out drops the identity
       * and leaves the balance where it was earned. The prompt stays marked as
       * seen, so it does not open itself again the moment the guest leaves.
       */
      signOut: () =>
        set({
          contactEmail: null,
          contactPhone: null,
          identityVerified: false,
        }),

      markIdentityPromptSeen: () => set({ identityPromptSeen: true }),

      expireIfStale: () => {
        const { lastActivityAt, points } = get();
        if (!lastActivityAt || points === 0) return;
        if (monthsSince(lastActivityAt) >= POINTS_VALIDITY_MONTHS) {
          set({ points: 0 });
        }
      },

      reset: () => set({ ...initialGuest }),
    }),
    {
      name: "loyalty-guest",
      version: 6,
      partialize: (state) => ({
        lastActivityAt: state.lastActivityAt,
        points: state.points,
        hashes: state.hashes,
        pending: state.pending,
        contactEmail: state.contactEmail,
        contactPhone: state.contactPhone,
        identityVerified: state.identityVerified,
        identityPromptSeen: state.identityPromptSeen,
      }),
      // Older payloads are carried forward rather than discarded: a bumped
      // version with no migration would wipe every guest's balance.
      // v1 predates `pending`, v2 held a single dishId per redemption.
      migrate: (persisted, version) => {
        if (version === 6) return persisted as LoyaltyGuest;
        if (version === 5) {
          return {
            ...(persisted as LoyaltyGuest),
            identityVerified: false,
            identityPromptSeen: false,
          };
        }
        if (version === 4) {
          // v4 held both in one field; route it by shape.
          const guest = persisted as LoyaltyGuest & { contact?: string | null };
          const held = guest.contact ?? null;
          return {
            ...guest,
            contactEmail: held?.includes("@") ? held : null,
            contactPhone: held && !held.includes("@") ? held : null,
            identityVerified: false,
            identityPromptSeen: false,
          };
        }
        if (version === 3) {
          return {
            ...(persisted as LoyaltyGuest),
            contactEmail: null,
            contactPhone: null,
            identityVerified: false,
            identityPromptSeen: false,
          };
        }
        if (version === 2) {
          const guest = persisted as LoyaltyGuest & {
            pending?: (PendingRedemption & { dishId?: string }) | null;
          };
          return {
            ...guest,
            contactEmail: null,
            contactPhone: null,
            identityVerified: false,
            identityPromptSeen: false,
            pending: guest.pending
              ? {
                  ...guest.pending,
                  dishIds: guest.pending.dishId ? [guest.pending.dishId] : [],
                }
              : null,
          };
        }
        if (version === 1) {
          return {
            ...(persisted as LoyaltyGuest),
            pending: null,
            contactEmail: null,
            contactPhone: null,
            identityVerified: false,
            identityPromptSeen: false,
          };
        }
        return undefined;
      },
    },
  ),
);

export const useLoyaltyPoints = () => useLoyaltyStore((state) => state.points);
export const useLoyaltyAvailablePoints = () =>
  useLoyaltyStore((state) => availableOf(state));
export const useLoyaltyPending = () => useLoyaltyStore((state) => state.pending);
export const useIdentityVerified = () =>
  useLoyaltyStore((state) => state.identityVerified);
export const useIdentityPromptSeen = () =>
  useLoyaltyStore((state) => state.identityPromptSeen);
/** What the guest left, e-mail first. Null until they leave something. */
export const useLoyaltyContact = () =>
  useLoyaltyStore((state) => state.contactEmail ?? state.contactPhone);
export const useHasLoyaltyContact = () =>
  useLoyaltyStore((state) =>
    Boolean(state.contactEmail || state.contactPhone),
  );
export const useLoyaltyHashes = () => useLoyaltyStore((state) => state.hashes);
export const useLoyaltyLastActivity = () =>
  useLoyaltyStore((state) => state.lastActivityAt);

export const useLoyaltyActions = () =>
  useLoyaltyStore(
    useShallow((state) => ({
      registerPayment: state.registerPayment,
      redeem: state.redeem,
      startRedemption: state.startRedemption,
      settleRedemption: state.settleRedemption,
      cancelRedemption: state.cancelRedemption,
      releaseExpiredRedemption: state.releaseExpiredRedemption,
      saveContact: state.saveContact,
      verifyIdentity: state.verifyIdentity,
      signOut: state.signOut,
      markIdentityPromptSeen: state.markIdentityPromptSeen,
      expireIfStale: state.expireIfStale,
      reset: state.reset,
    })),
  );
